import { useCallback, useRef, useState } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';
import {
  buildLiveHealthMapFromGameState,
  shouldClearHealthFxSession,
} from './battlefieldHealthDisplay';

const COUNTDOWN_STEP_MS = 65;
const COUNTDOWN_MIN_TOTAL_MS = 350;
const COUNTDOWN_MAX_TOTAL_MS = 850;
const HEALTH_COUNTDOWN_TICK_SOUND = '/sounds/health_lost.mp3';
const HEALTH_COUNTDOWN_TICK_VOLUME = 0.6;

function playHealthCountdownTickSound() {
  playSoundEffect(HEALTH_COUNTDOWN_TICK_SOUND, HEALTH_COUNTDOWN_TICK_VOLUME);
}

function createFxEntry(displayHealth, { pulsing = false, targetHealth, startedAt = Date.now() } = {}) {
  return {
    displayHealth,
    pulsing,
    targetHealth: typeof targetHealth === 'number' ? targetHealth : undefined,
    startedAt,
  };
}

/**
 * Cosmetic HP badge FX after damage — never mutates gameState.
 * Badge reads via badgeHealth; unit.health stays authoritative.
 */
export function useBattlefieldHealthCountdown(animationSpeedMultiplier = 1) {
  const displaysRef = useRef(new Map());
  const timersRef = useRef(new Map());
  const [tick, setTick] = useState(0);

  const bump = () => setTick((value) => value + 1);

  const clearCountdown = useCallback((instanceId) => {
    if (!instanceId) return;
    const timers = timersRef.current.get(instanceId);
    if (timers) {
      if (timers.intervalId) clearInterval(timers.intervalId);
      if (timers.cleanupId) clearTimeout(timers.cleanupId);
      timersRef.current.delete(instanceId);
    }
    if (displaysRef.current.has(instanceId)) {
      displaysRef.current.delete(instanceId);
      bump();
    }
  }, []);

  const setHealthHold = useCallback((instanceId, healthBefore, targetHealth) => {
    if (!instanceId || typeof healthBefore !== 'number') return;
    clearCountdown(instanceId);
    displaysRef.current.set(
      instanceId,
      createFxEntry(healthBefore, { targetHealth })
    );
    bump();
  }, [clearCountdown]);

  const startCountdown = useCallback((instanceId, fromHealth, toHealth) => {
    if (!instanceId || typeof fromHealth !== 'number' || typeof toHealth !== 'number') {
      return;
    }
    if (toHealth <= 0 || fromHealth <= toHealth) {
      clearCountdown(instanceId);
      return;
    }

    clearCountdown(instanceId);
    const speedFactor = animationSpeedMultiplier > 0 ? animationSpeedMultiplier : 1;
    const totalSteps = fromHealth - toHealth;
    const totalDuration = Math.min(
      COUNTDOWN_MAX_TOTAL_MS,
      Math.max(COUNTDOWN_MIN_TOTAL_MS, Math.round((totalSteps * COUNTDOWN_STEP_MS) / speedFactor))
    );
    const stepMs = Math.max(35, Math.round(totalDuration / totalSteps));

    let current = fromHealth;
    const startedAt = Date.now();
    displaysRef.current.set(
      instanceId,
      createFxEntry(current, { pulsing: true, targetHealth: toHealth, startedAt })
    );
    bump();

    const intervalId = setInterval(() => {
      current -= 1;
      playHealthCountdownTickSound();
      if (current <= toHealth) {
        clearInterval(intervalId);
        displaysRef.current.set(
          instanceId,
          createFxEntry(toHealth, { pulsing: true, targetHealth: toHealth, startedAt })
        );
        bump();
        const cleanupId = setTimeout(() => {
          displaysRef.current.delete(instanceId);
          timersRef.current.delete(instanceId);
          bump();
        }, Math.round(220 / speedFactor));
        timersRef.current.set(instanceId, { intervalId: null, cleanupId });
        return;
      }
      displaysRef.current.set(
        instanceId,
        createFxEntry(current, { pulsing: true, targetHealth: toHealth, startedAt })
      );
      bump();
    }, stepMs);

    timersRef.current.set(instanceId, { intervalId, cleanupId: null });
  }, [animationSpeedMultiplier, clearCountdown]);

  const getHealthDisplayOverride = useCallback((instanceId) => {
    void tick;
    if (!instanceId) return null;
    const entry = displaysRef.current.get(instanceId);
    if (!entry) return null;
    return { health: entry.displayHealth, pulse: entry.pulsing };
  }, [tick]);

  const getHealthFxEntry = useCallback((instanceId) => {
    void tick;
    if (!instanceId) return null;
    return displaysRef.current.get(instanceId) ?? null;
  }, [tick]);

  const reconcileAll = useCallback((liveHealthByInstanceId, context) => {
    let changed = false;
    for (const [instanceId, entry] of displaysRef.current) {
      const liveHealth = liveHealthByInstanceId.get(instanceId);
      if (shouldClearHealthFxSession(instanceId, entry, liveHealth, context)) {
        clearCountdown(instanceId);
        changed = true;
      }
    }
    return changed;
  }, [clearCountdown]);

  const reconcileFromGameState = useCallback((gameState, context) => {
    return reconcileAll(buildLiveHealthMapFromGameState(gameState), context);
  }, [reconcileAll]);

  const resetAll = useCallback(() => {
    for (const [, timers] of timersRef.current) {
      if (timers.intervalId) clearInterval(timers.intervalId);
      if (timers.cleanupId) clearTimeout(timers.cleanupId);
    }
    timersRef.current.clear();
    displaysRef.current.clear();
    bump();
  }, []);

  return {
    setHealthHold,
    startCountdown,
    getHealthDisplayOverride,
    getHealthFxEntry,
    reconcileAll,
    reconcileFromGameState,
    clearCountdown,
    resetAll,
  };
}

export default useBattlefieldHealthCountdown;
